/**
 * §P.4 · Lo que ha entrado.
 *
 * ## Para qué sirve, que no es lo mismo que la lista de recibos
 *
 * Un recibo es lo que el club **pidió**; un cobro es lo que **entró**. Son dos listas
 * distintas y la segunda solo se podía ver abriendo recibo por recibo, que es
 * exactamente lo que nadie hace a final de mes.
 *
 * Esta pantalla existe para una tarea concreta: **cuadrar con el extracto del
 * banco**. Por eso la referencia va en la tabla y no escondida —«el concepto del
 * Bizum, los últimos dígitos del teléfono, "en mano el martes"»— y por eso se suma lo
 * que se está viendo.
 *
 * ## La suma es de la página, y se dice
 *
 * Con paginación por cursor no hay forma de sumar el total sin recorrerlo entero, y
 * pedir eso al servidor sería una ruta que no existe. Así que se suma lo que hay
 * delante y **se dice que es de esta página**: una cifra que parece el total del mes
 * y es el de veinticinco filas es peor que no poner ninguna.
 *
 * ## Quién lo marcó, delante
 *
 * En efectivo o Bizum lo da por cobrado una persona. «Es dinero, y las familias
 * reclaman»: el día que alguien pregunte por qué figura pagado marzo, la respuesta
 * tiene que estar en la misma fila.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { cuenta, euros, fecha, fechaYHora } from '@/shared/ui/formato';
import {
  anterior,
  cursorActual,
  esLaPrimera,
  numeroDePagina,
  PRIMERA,
  siguiente,
  type PilaDeCursores,
} from '@/shared/ui/paginas';
import { etiquetaDeMetodo, type Cobro } from '@/club/facturacion/tipos';

interface PaginaDeCobros {
  readonly data: readonly Cobro[];
  readonly next_cursor: string | null;
  readonly has_more: boolean;
}

export function LoCobrado({
  club,
  alAbrirRecibo,
}: {
  club: string;
  alAbrirRecibo: (reciboId: string) => void;
}) {
  const [cursores, setCursores] = useState<PilaDeCursores>(PRIMERA);
  const cursor = cursorActual(cursores);

  const cobros = useQuery({
    queryKey: ['cobros', club, cursor],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).payments.get({
        query: { limit: 25, ...(cursor === null ? {} : { cursor }) },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as PaginaDeCobros;
    },
  });

  if (cobros.isPending) {
    return <Cargando filas={6} etiqueta="Cargando los cobros" />;
  }

  if (cobros.isError || !cobros.data) {
    return (
      <PanelError
        problema={aProblema(cobros.error)}
        reintentar={() => void cobros.refetch()}
      />
    );
  }

  const enPantalla = cobros.data.data.reduce((s, c) => s + c.amount_cents, 0);

  return (
    <section>
      <h1>Lo que ha entrado</h1>

      <p className="tenue">
        Todos los cobros, del más reciente al más antiguo.{' '}
        <strong>Es la lista con la que se cuadra el extracto del banco</strong>:
        cada uno con su referencia y con quién lo dio por cobrado.
      </p>

      {cobros.data.data.length === 0 ? (
        <Vacio
          titulo="Todavía no ha entrado nada"
          explicacion="Un cobro se anota desde el recibo, cuando la familia paga por Bizum, transferencia o en mano. Aquí aparecen todos juntos."
        />
      ) : (
        <>
          <div className="tabla-marco">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Entró</th>
                  <th className="num">Importe</th>
                  <th>Cómo</th>
                  <th>Referencia</th>
                  <th>Lo marcó</th>
                  <th>Recibo</th>
                </tr>
              </thead>
              <tbody>
                {cobros.data.data.map((c) => (
                  <tr key={c.id}>
                    <td>
                      {/*
                        La fecha del dinero y no la del tecleo: un cobro se anota
                        días después. `charge_date` es cuándo entró; `created_at`,
                        cuándo se apuntó. Si no hay la primera, se dice.
                      */}
                      {c.charge_date ? (
                        fecha(c.charge_date)
                      ) : (
                        <span className="tenue">
                          sin fecha · anotado {fechaYHora(c.created_at)}
                        </span>
                      )}
                    </td>
                    <td className="num">
                      <strong>{euros(c.amount_cents)}</strong>
                    </td>
                    <td className="tenue">{etiquetaDeMetodo(c.method)}</td>
                    <td className="tenue">{c.reference ?? '—'}</td>
                    <td className="tenue">
                      {/*
                        `recorded_by` es un identificador, no un nombre: el contrato
                        no manda el correo en esta ruta. Decir «a mano» y «solo» es
                        más honesto que pintar un uuid de treinta y seis caracteres.
                      */}
                      {c.recorded_by ? 'A mano' : 'Automático'}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="enlace-fila"
                        onClick={() => alAbrirRecibo(c.invoice_id)}
                      >
                        Ver el recibo
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="tenue">
            {/*
              «De esta página» va delante y no en letra pequeña: una suma que parece
              el total del mes y es el de veinticinco filas descuadra una conciliación.
            */}
            En esta página: {cuenta(cobros.data.data.length, 'cobro', 'cobros')}{' '}
            por <strong>{euros(enPantalla)}</strong>. El total del mes está en
            el informe de ingresos.
          </p>

          {/* Por cursor, nunca por número de página. Ver `shared/ui/paginas`. */}
          <div className="paginacion">
            <button
              type="button"
              className="btn-plano"
              disabled={esLaPrimera(cursores) || cobros.isFetching}
              onClick={() => setCursores(anterior)}
            >
              Anterior
            </button>
            <span className="tenue">
              {esLaPrimera(cursores)
                ? 'Primera página'
                : `Página ${numeroDePagina(cursores)}`}
            </span>
            <button
              type="button"
              className="btn-plano"
              disabled={!cobros.data.has_more || cobros.isFetching}
              onClick={() =>
                setCursores((c) =>
                  siguiente(c, cobros.data?.next_cursor ?? null)
                )
              }
            >
              Siguiente
            </button>
          </div>
        </>
      )}
    </section>
  );
}
